import { EmailVerificationOverlay } from '@/components/EmailVerificationOverlay';
import { useOnboardingAnchor } from '@/features/experiences/components/PlannerOnboarding/anchorContext';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useDebounceLoading } from '@/hooks/useDebounceLoading';
import { useLauncherAction } from '@/hooks/useLauncher';
import { ExperienceTypes } from '@/types/gql.generated';
import {
  type ButtonProps,
  Box,
  Button,
  Center,
  Flex,
  Hr,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from '@/ui';
import { useCollaborators } from '../../hooks/useCollaborators';
import { AddToCalendarAccessNote } from './AddToCalendarAccessNote';
import { CollaboratorsSection } from './CollaboratorsSection';
import { CopyLinkSection } from './CopyLinkSection';
import { GeneralAccessSection } from './GeneralAccessSection';
import { MoveToWorkspaceLink } from './MoveToWorkspaceLink';
import { PublicLinkSection } from './PublicLinkSection';
import { useShareModalStore } from './store';

type LaunchParams = {
  email?: string;
};

type Props = {
  triggerProps?: ButtonProps;
};

export const ShareModal = ({ triggerProps }: Props) => {
  const { close, defaults, isOpen, isGuided, open } = useShareModalStore();
  const inviteAnchorRef = useOnboardingAnchor('shareInvite');
  const {
    collaborators,
    organizationPermissions,
    isLoading: isLoadingCollaborators,
  } = useCollaborators();
  const { experience } = usePageExperienceContext();
  const isPlanner = experience?.type === ExperienceTypes.Planner;

  const debouncedIsLoadingCollaborators = useDebounceLoading(
    isLoadingCollaborators,
    250
  );

  useLauncherAction('share-modal', (params: LaunchParams) => open(params));

  return (
    <>
      <Button
        aria-label="Share"
        borderRadius="full"
        fontWeight="bold"
        gap={1.5}
        size="sm"
        {...triggerProps}
        onClick={(e) => {
          triggerProps?.onClick?.(e);
          open();
        }}
      >
        {/* Icon-only on mobile (where Customize is hidden); the "Share" label
            takes over from md up, so the icon drops away to keep it clean. */}
        <Icon display={{ base: 'block', md: 'none' }} icon="Share2" size={4} />
        <Box as="span" display={{ base: 'none', md: 'inline' }}>
          Share
        </Box>
      </Button>

      <Modal
        isCentered
        isOpen={isOpen}
        returnFocusOnClose={false}
        // The guide keeps its own card on screen beside the modal; trapping
        // focus here would pull it back off that card's Next button.
        trapFocus={!isGuided}
        variant="primary"
        onClose={close}
      >
        <ModalOverlay />

        <ModalContent
          data-testid="share-modal"
          maxH="var(--app-height)"
          minW={{ base: '100vw', md: '600px' }}
        >
          <ModalHeader>Share</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <EmailVerificationOverlay>
              {debouncedIsLoadingCollaborators ? (
                <Center data-testid="share-loader" h="75px">
                  <Spinner />
                </Center>
              ) : (
                <Flex direction="column" gap="8">
                  {isPlanner ? (
                    <PublicLinkSection />
                  ) : (
                    <>
                      <CopyLinkSection />
                      {/* Sits right under the link so an owner sees it as soon
                          as they share; self-gates to a public schedule whose
                          Send-to-calendar audience is narrower than the link. */}
                      <AddToCalendarAccessNote />
                      <Hr mx="-8" />
                    </>
                  )}
                  <Box ref={inviteAnchorRef}>
                    <CollaboratorsSection
                      collaborators={collaborators}
                      defaultEmail={defaults.email}
                    />
                  </Box>
                  <GeneralAccessSection
                    organizationPermissions={organizationPermissions}
                  />
                  {/* Mutually exclusive with the section above: General Access
                      renders for a shared home, this link for a personal one. */}
                  <MoveToWorkspaceLink />
                </Flex>
              )}
            </EmailVerificationOverlay>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
